import {
  EDataBaseType,
  EProjectType,
  EScreenType,
  ESdChangeReason,
  IProcessStepScreensInfo
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import LocalStorageService from "../../../../auth/local-storage-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { EScreenRole, resolveScreenRole } from "../../../../shared/utility/screen-role-helper";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInfoBox from "../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import PropertyRowIcon from "./property-row-icon";
import { ScreenProcessStepConfig } from "./screen-process-step-config";

export interface IScreenPropertiesState {
  selectedRole: EScreenRole;
}

export interface IScreenPropertiesProps {}

class ScreenProperties extends PureSdComponent<
  IScreenPropertiesProps & IWithSDProps & IWithContextProp,
  IScreenPropertiesState
> {
  private readonly isSqlServerWorkspace: boolean = false;

  private readonly isOracleWorkspace: boolean = false;

  constructor(props: IScreenPropertiesProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      selectedRole: this.getSelectedRoleForScreen()
    };
    this.isSqlServerWorkspace = LocalStorageService.getCurrentDomainManagedDbType() === EDataBaseType.MSSQL;
    this.isOracleWorkspace = LocalStorageService.getCurrentDomainManagedDbType() === EDataBaseType.Oracle;
  }

  componentDidMount(): void {
    const processStepScreen = this.props.sd.project.processStepScreenIds?.find(
      (p) => p.screenId === this.props.sd.sdId
    );
  }

  componentDidUpdate(
    prevProps: Readonly<IScreenPropertiesProps & IWithSDProps & IWithContextProp>,
    prevState: Readonly<IScreenPropertiesState>,
    snapshot?: any
  ): void {
    if (prevProps.sd.sdId !== this.props.sd.sdId) {
      this.setState({
        selectedRole: this.getSelectedRoleForScreen()
      });
      return;
    }
    // A role set somewhere else (another screen taking Starting Screen, an undo, a collaborator)
    // arrives as a new project. Only then is the saved role the newer truth; while this panel's own
    // change is still saving, the project is the old one and the picked role must stand.
    if (prevProps.sd.project !== this.props.sd.project) {
      const savedRole: EScreenRole = this.getSelectedRoleForScreen();
      if (savedRole !== this.state.selectedRole) {
        this.setState({ selectedRole: savedRole });
      }
    }
  }

  handleChangeMasterScreen = (e: any, masterId: string | undefined) => {
    if (masterId) {
      this.props.sd.setMasterScreen(masterId);
    } else {
      this.props.sd.setMasterScreen(undefined);
    }
  };

  handleChangeOfflineScreen = (screenId: string, value: boolean) => {
    this.props.sd.setScreenAsOfflineScreen(screenId, value);
  };

  handleChangeScreenShouldRefresh = (screenId: string, value: boolean) => {
    this.props.sd.setScreenShouldRefresh(screenId, value);
  };

  handleChangeScreenDataCacheEnabled = (screenId: string, value: boolean) => {
    this.props.sd.setScreenDataCacheEnabled(screenId, value);
  };

  getSelectedRoleForScreen = (): EScreenRole => {
    return resolveScreenRole(this.props.sd.sdId, this.props.sd.project);
  };

  // The row's value reads the same role the menu marks. It used to re-read the saved project, which
  // still held the old role until the save came back, so a pick showed its check mark in the menu
  // and the previous role on the row.
  getSelectedRoleFriendlyNameForScreen = (): string => {
    const role: EScreenRole = this.state.selectedRole;
    return KMainFunctions.convertCamelCaseToFriendlyName(EScreenRole[role]);
  };

  handleScreenRoleChanged = async (val) => {
    const project = _.cloneDeep(this.props.sd.project);
    if (project.startingScreenId === this.props.sd.sdId) project.startingScreenId = undefined;
    if (project.signinScreenId === this.props.sd.sdId) project.signinScreenId = undefined;
    if (project.resetPasswordScreenId === this.props.sd.sdId) project.resetPasswordScreenId = undefined;
    if (project.splashScreenId === this.props.sd.sdId) project.splashScreenId = undefined;
    if (project.newPasswordScreenId === this.props.sd.sdId) project.newPasswordScreenId = undefined;
    if (project.verificationCodeScreenId === this.props.sd.sdId) project.verificationCodeScreenId = undefined;
    if (project.offlineScreenId === this.props.sd.sdId) {
      project.offlineScreenId = undefined;
      // Also clear supportOffline when removing offline screen role
      this.props.sd.setScreenAsOfflineScreen(this.props.sd.sdId, false);
    }
    if (project.documentUploadLinkScreenId === this.props.sd.sdId) project.documentUploadLinkScreenId = undefined;

    type ScreenRole = (typeof EScreenRole)[keyof typeof EScreenRole];
    var role = Number(val) as ScreenRole;
    this.setState({ selectedRole: role });

    switch (Number(val)) {
      case EScreenRole.startingScreen:
        project.startingScreenId = this.props.sd.sdId;
        break;
      case EScreenRole.signinScreen:
        project.signinScreenId = this.props.sd.sdId;
        break;
      case EScreenRole.resetPasswordScreen:
        project.resetPasswordScreenId = this.props.sd.sdId;
        break;
      case EScreenRole.splashScreen:
        project.splashScreenId = this.props.sd.sdId;
        break;
      case EScreenRole.newPasswordScreen:
        project.newPasswordScreenId = this.props.sd.sdId;
        break;
      case EScreenRole.verificationScreen:
        project.verificationCodeScreenId = this.props.sd.sdId;
        break;
      case EScreenRole.offlineScreen:
        project.offlineScreenId = this.props.sd.sdId;
        // Also set supportOffline to true for the offline screen
        this.props.sd.setScreenAsOfflineScreen(this.props.sd.sdId, true);
        break;
      case EScreenRole.documentUploadLinkScreen:
        project.documentUploadLinkScreenId = this.props.sd.sdId;
        break;

      default:
        break;
    }

    if (!(Number(val) === EScreenRole.processStepScreen || Number(val) === EScreenRole.extProcessStepScreen)) {
      const itemIndex = project.processStepScreenIds?.findIndex((i) => i.screenId === this.props.sd.sdId);
      if (itemIndex > -1) {
        project.processStepScreenIds.splice(itemIndex, 1);
      }
    }
    await this.props.sd.updateProject(project);
  };

  authorizedRolesChanged = (role) => {
    this.props.sd.updateScreenAuthorization(role);
  };

  isRoleAuthorized = (role): boolean => {
    const sd = this.props.sd.getScreenDesign();
    return sd.authorization != null && sd.authorization.permittedRoles?.includes(role.id);
  };

  isHiddenChanged = () => {
    this.props.sd.isHiddenChanged();
  };

  isHidden = () => {
    const sd = this.props.sd.getScreenDesign();
    return sd.authorization != null && sd.authorization.hide;
  };

  isDisabledChanged = () => {
    this.props.sd.isDisabledChanged();
  };

  isDisabled = () => {
    const sd = this.props.sd.getScreenDesign();
    return sd.authorization != null && sd.authorization.disable;
  };

  getClassnameForActiveStatus = (a): string => {
    if (a.isActive) return "kuika_rotate90deg";
    return "kuika_rotate0deg";
  };

  UpdateProcessStepScreensInfo = async (info: IProcessStepScreensInfo | undefined) => {
    const project = this.props.sd.getProject();
    var processStepScreenIds = _.clone(project.processStepScreenIds) || [];

    processStepScreenIds = processStepScreenIds.filter((p) => p.projectId && p.processId && p.stepId && p.screenId);
    if (info == undefined) {
      const itemIndex = processStepScreenIds.findIndex((i) => i.screenId === this.props.sd.sdId);
      if (itemIndex > -1) {
        processStepScreenIds.splice(itemIndex, 1);
      }
    } else {
      const itemIndex = processStepScreenIds.findIndex((i) => i.screenId === this.props.sd.sdId);
      if (itemIndex > -1) {
        processStepScreenIds[itemIndex] = _.cloneDeep(info);
      } else {
        processStepScreenIds.push(_.cloneDeep(info));
      }
    }

    project.processStepScreenIds = processStepScreenIds;
    await this.props.sd.updateProject(project);
  };

  getRoleMenuOptions = (): KuikaDropdownMenuItemType[] => {
    const options: KuikaDropdownMenuItemType[] = [
      {
        key: EScreenRole.none,
        label: "None",
        icon: <KuikaSvg name="file" type="sidebar-icons" width={16} height={16} />,
        showSelectedIcon: true
      },
      {
        key: EScreenRole.startingScreen,
        label: "Starting Screen",
        icon: <KuikaSvg name="home" width={16} height={16} />,
        showSelectedIcon: true
      },
      {
        key: EScreenRole.signinScreen,
        label: "Signin Screen",
        icon: <KuikaSvg name="login-2" width={16} height={16} />,
        showSelectedIcon: true
      },
      {
        key: EScreenRole.resetPasswordScreen,
        label: "Reset Password Screen",
        icon: <KuikaSvg name="lock-open-2" width={16} height={16} />,
        showSelectedIcon: true
      }
    ];

    if (this.props.sd.project.projectType != EProjectType.Desktop) {
      options.push({
        key: EScreenRole.splashScreen,
        label: "Splash Screen",
        icon: <KuikaSvg name="device-mobile" width={16} height={16} />,
        showSelectedIcon: true
      });
    }

    options.push({
      key: EScreenRole.newPasswordScreen,
      label: "New Password Screen",
      icon: <KuikaSvg name="lock-plus" width={16} height={16} />,
      showSelectedIcon: true
    });

    options.push({
      key: EScreenRole.verificationScreen,
      label: "Verification Screen",
      icon: <KuikaSvg name="shield-check" width={16} height={16} />,
      showSelectedIcon: true
    });

    options.push({
      key: EScreenRole.offlineScreen,
      label: "Default Offline Screen",
      icon: <KuikaSvg name="wifi-off" width={16} height={16} />,
      showSelectedIcon: true
    });

    options.push({
      key: EScreenRole.documentUploadLinkScreen,
      label: "Document Upload Link Screen",
      icon: <KuikaSvg name="upload" width={16} height={16} />,
      showSelectedIcon: true
    });

    return options;
  };

  getMasterScreenMenuOptions = (): KuikaDropdownMenuItemType[] => {
    const options: KuikaDropdownMenuItemType[] = [
      {
        key: "none",
        label: "None",
        icon: <KuikaSvg name="file" type="sidebar-icons" width={16} height={16} />,
        showSelectedIcon: true
      }
    ];

    if (this.props.sd.screenList) {
      this.props.sd.screenList
        .filter((screen) => screen.publicMetaData?.screenType === EScreenType.Master)
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((screen) => {
          options.push({
            key: screen.id,
            label: (
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {screen.name}
                {screen.publicMetaData?.supportOffline ? (
                  <KuikaSvg name="wifi-off" width={16} height={16} />
                ) : (
                  <KuikaSvg name="wifi" width={16} height={16} />
                )}
              </div>
            ),
            dataTestId: `masterScreenOption-${screen.name}`,
            icon: <KuikaSvg name="layout-board" width={16} height={16} />,
            showSelectedIcon: true
          });
        });
    }

    return options;
  };

  render() {
    const selectedRole = this.state.selectedRole;
    const screenPublicData = this.props.sd.getScreenPublic(this.props.sd.sdId);
    const isMasterScreenBindedPage = screenPublicData?.masterId && screenPublicData.screenType !== EScreenType.Master;
    const showOfflineScreenSupportSwitch =
      !KMainFunctions.isMainAI() &&
      (selectedRole === EScreenRole.none || selectedRole === EScreenRole.startingScreen) &&
      this.isSqlServerWorkspace &&
      !isMasterScreenBindedPage;
    const isStepScreenInAnyProcess = this.props?.sd?.project?.processStepScreenIds.find(
      (x) => x?.screenId === this.props?.sd?.sdId
    );
    const masterScreenIdOfScreen = this.props.sd.getMasterScreenId(this.props.sd.screenList);
    const roleMenuOptions = this.getRoleMenuOptions();

    return (
      <>
        <div className="kuika_Wrapper">
          <div className="kuika_actionPanelWrapperHeader kuika-right-panel__group">
            {!isStepScreenInAnyProcess && (
              <div className="kuika-right-panel__card--wrapper">
                {this.props.sd.getScreenType() !== EScreenType.Master ? (
                  <KuikaDropdownMenu
                    options={roleMenuOptions}
                    selected={Number(selectedRole)}
                    setSelected={(value) => this.handleScreenRoleChanged(value)}
                    trigger="click"
                  >
                    <div
                      className="kuika-right-panel__card-darker"
                      data-onboarding-id="kuika-right-panel_role_dropdown"
                    >
                      <PropertyRowIcon kind="role" />
                      <div className="kuika-right-panel__card__title">
                        <KuikaTextMedium>Role</KuikaTextMedium>
                        <KuikaSmallText>{this.getSelectedRoleFriendlyNameForScreen()}</KuikaSmallText>
                      </div>
                      <KuikaButton isIcon type="tertiary" color="neutral" size="sm">
                        <KuikaSvg name="chevron-down" type="arrows" />
                      </KuikaButton>
                    </div>
                  </KuikaDropdownMenu>
                ) : (
                  <div className="kuika-right-panel__card">
                    <PropertyRowIcon kind="role" />
                    <div className="kuika-right-panel__card__title">
                      <KuikaTextMedium>Role</KuikaTextMedium>
                      <KuikaSmallText>Master Screen</KuikaSmallText>
                    </div>
                  </div>
                )}
              </div>
            )}
            {(masterScreenIdOfScreen !== undefined ||
              (this.props.context.isMasterScreenActive &&
                screenPublicData?.screenType !== EScreenType.Master &&
                this.props.sd.screenList?.filter((x) => x.publicMetaData?.screenType === EScreenType.Master)?.length >
                  0)) && (
              <div className="kuika-right-panel__card--wrapper">
                <KuikaDropdownMenu
                  options={this.getMasterScreenMenuOptions()}
                  selected={this.props.sd.getMasterScreenId(this.props.sd.screenList) || "none"}
                  setSelected={(value) => {
                    if (value === "none") {
                      this.handleChangeMasterScreen(null, undefined);
                    } else {
                      this.handleChangeMasterScreen(null, String(value));
                    }
                  }}
                  trigger="click"
                >
                  <div
                    className={`kuika-right-panel__card-darker${
                      isMasterScreenBindedPage &&
                      screenPublicData?.masterId &&
                      this.props.sd.getScreenPublic(screenPublicData.masterId)?.supportOffline
                        ? " kuika-right-panel__card--open"
                        : ""
                    }`}
                    data-onboarding-id="kuika-right-panel_master_screen_dropdown"
                    data-testid="masterScreenDropdown"
                  >
                    <PropertyRowIcon kind="masterScreen" />
                    <div className="kuika-right-panel__card__title">
                      <KuikaTextMedium>Master Screen</KuikaTextMedium>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <KuikaSmallText>
                          {masterScreenIdOfScreen
                            ? this.props.sd.screenList?.find((x) => x.id === masterScreenIdOfScreen)?.name
                            : "None"}
                        </KuikaSmallText>
                        {masterScreenIdOfScreen ? (
                          this.props.sd.getScreenPublic(masterScreenIdOfScreen || "")?.supportOffline ? (
                            <KuikaSvg name="wifi-off" width={16} height={16} />
                          ) : (
                            <KuikaSvg name="wifi" width={16} height={16} />
                          )
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>
                    <KuikaButton isIcon type="tertiary" color="neutral" size="sm">
                      <KuikaSvg name="chevron-down" type="arrows" />
                    </KuikaButton>
                  </div>
                </KuikaDropdownMenu>
                {isMasterScreenBindedPage &&
                  screenPublicData.masterId &&
                  this.props.sd.getScreenPublic(screenPublicData.masterId)?.supportOffline && (
                    <div className="kuika-right-panel__card-body">
                      <KuikaInfoBox
                        leftUnit={<KuikaSvg name="circle-warning" width={16} height={16} />}
                        rightUnit={
                          <KuikaSmallText>
                            Due to the selected master screen, the related screen that opens will also operate in
                            offline mode.
                          </KuikaSmallText>
                        }
                      />
                    </div>
                  )}
              </div>
            )}
            {
              <ScreenProcessStepConfig
                selectedRole={this.getSelectedRoleForScreen()}
                editable={false}
                onStepPropertiesChanged={async (info) => this.UpdateProcessStepScreensInfo(info)}
              ></ScreenProcessStepConfig>
            }
            {showOfflineScreenSupportSwitch && (
              <div className="kuika-right-panel__card--wrapper">
                <div className="kuika-right-panel__card">
                  <PropertyRowIcon kind="offline" />
                  <div className="kuika-right-panel__card__title">
                    <KuikaTextMedium>Works Offline</KuikaTextMedium>
                  </div>
                  <div className="kuika-right-panel__card__content">
                    <KuikaSwitch
                      dataTestId="isOfflineSwitch"
                      checked={
                        this.props.sd.screenList?.find((x) => x.id === this.props.sd.sdId)?.publicMetaData
                          ?.supportOffline
                      }
                      onChange={(checked) => this.handleChangeOfflineScreen(this.props.sd.sdId, checked)}
                    />
                  </div>
                </div>
              </div>
            )}
            {this.props.sd.project.projectType === EProjectType.Mobile &&
              this.props.sd.getScreenType() !== EScreenType.Master &&
              this.props.sd.getScreenType() !== EScreenType.WebScreen && (
                <div className="kuika-right-panel__card--wrapper">
                  <div className="kuika-right-panel__card">
                    <PropertyRowIcon kind="pullToRefresh" />
                    <div className="kuika-right-panel__card__title">
                      <KuikaTextMedium>Pull to Refresh</KuikaTextMedium>
                    </div>
                    <div className="kuika-right-panel__card__content">
                      <KuikaSwitch
                        dataTestId="pullToRefreshEnabledSwitch"
                        checked={
                          this.props.sd.screenList?.find((x) => x.id === this.props.sd.sdId)?.publicMetaData
                            ?.shouldRefresh !== false
                        }
                        onChange={(checked) => this.handleChangeScreenShouldRefresh(this.props.sd.sdId, checked)}
                      />
                    </div>
                  </div>
                </div>
              )}
            {this.props.sd.project.projectType === EProjectType.Mobile &&
              this.props.sd.getScreenType() !== EScreenType.Master &&
              this.props.sd.getScreenType() !== EScreenType.WebScreen && (
                <div className="kuika-right-panel__card--wrapper">
                  <div className="kuika-right-panel__card">
                    <PropertyRowIcon kind="dataCache" />
                    <div className="kuika-right-panel__card__title">
                      <KuikaTextMedium>Data Cache</KuikaTextMedium>
                    </div>
                    <div className="kuika-right-panel__card__content">
                      <KuikaSwitch
                        dataTestId="dataCacheEnabledSwitch"
                        checked={
                          this.props.sd.screenList?.find((x) => x.id === this.props.sd.sdId)?.publicMetaData
                            ?.dataCacheEnabled === true
                        }
                        onChange={(checked) => this.handleChangeScreenDataCacheEnabled(this.props.sd.sdId, checked)}
                      />
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock
    ) {
      return true;
    }
    return false;
  };
}

const screenProperties = withContext<IScreenPropertiesProps>(withSD<IScreenPropertiesProps>(ScreenProperties));
export { screenProperties as ScreenProperties };
